import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add, PersonAdd } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import Loading from "components/Loading";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import TciRegistrySelect, { useTciRegistryOptions } from "components/fde/TciRegistrySelect";
import { useAuth } from "hooks/useAuth";
import { assignFdeRole, revokeFdeRole } from "services/fde/fdeService";
import {
  FDE_ROLE_LABELS,
  FDE_STATUS_LABELS,
  listClinicalDomains,
  listFdeAssignments,
} from "services/fde/fdeRegistryService";
import {
  generateHierarchyInvite,
  listSentHierarchyInvites,
  revokeHierarchyInvite,
} from "services/fde/hierarchyInviteService";
import { db } from "services/firebase";
import { PROMPT_LAYER_LABELS } from "services/llm/hierarchyPromptScope";
import { isFirestorePermissionDenied } from "utils/firestoreErrors";

const EMPTY_ASSIGN_FORM = {
  uid: "",
  role: "fde-c",
  scopeLevel: 3,
  scopeId: "",
  tciId: "",
  domainId: "",
  credentialAttestation: "",
  trainingComplete: false,
};

const SCOPE_LEVEL_OPTIONS = [
  { value: 3, label: "Institution-wide" },
  { value: 4, label: "Sub-institution" },
  { value: 5, label: "Department" },
  { value: 6, label: "Sub-department" },
];

const FdeRosterPage = () => {
  const { user, organizationId, userData, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;
  const canManage =
    Boolean(isGlobalAdmin) ||
    (userData?.role === "admin" && Boolean(institutionId));

  const [assignments, setAssignments] = useState([]);
  const [domains, setDomains] = useState([]);
  const { tcis, tciNameById, loading: tciRegistryLoading } = useTciRegistryOptions({
    institutionId,
  });
  const [hierarchyNodes, setHierarchyNodes] = useState([]);
  const [scopeNameById, setScopeNameById] = useState({});
  const [userNames, setUserNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState("");

  const {
    control: inviteControl,
    handleSubmit: handleInviteSubmit,
    reset: resetInviteForm,
  } = useForm({
    defaultValues: {
      assignedRole: "fde-c",
      targetLevel: 3,
      tciId: "",
      domainId: "",
      expiryDays: 14,
    },
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_ASSIGN_FORM });

  const watchedRole = watch("role");

  const loadData = useCallback(async () => {
    if (!institutionId) {
      setAssignments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [assignmentRows, domainRows, nodesSnap] = await Promise.all([
        listFdeAssignments({ institutionId }),
        listClinicalDomains({ institutionId }),
        db
          .collection("hierarchyNodes")
          .where("institutionId", "==", institutionId)
          .where("isActive", "==", true)
          .get(),
      ]);
      setAssignments(assignmentRows);
      setDomains(domainRows);
      const nodes = nodesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setHierarchyNodes(nodes);

      const scopeIds = [
        ...new Set(
          [
            institutionId,
            ...assignmentRows.map((row) => row.scopeId),
            ...nodes.map((node) => node.id),
          ].filter(Boolean),
        ),
      ];
      const scopeNameEntries = await Promise.all(
        scopeIds.map(async (scopeId) => {
          const fromNode = nodes.find((node) => node.id === scopeId);
          if (fromNode?.name) return [scopeId, fromNode.name];
          try {
            const orgDoc = await db.collection("organizations").doc(scopeId).get();
            const orgName = orgDoc.data()?.name;
            if (orgName) return [scopeId, orgName];
          } catch (err) {
            if (!isFirestorePermissionDenied(err)) {
              console.warn("[FdeRosterPage] scope name lookup failed", scopeId, err);
            }
          }
          return [scopeId, null];
        }),
      );
      setScopeNameById(
        Object.fromEntries(scopeNameEntries.filter(([, label]) => Boolean(label))),
      );

      const uids = [...new Set(assignmentRows.map((row) => row.uid).filter(Boolean))];
      const nameEntries = await Promise.all(
        uids.map(async (uid) => {
          try {
            const userDoc = await db.collection("users").doc(uid).get();
            const data = userDoc.data() || {};
            const label =
              [data.firstName, data.lastName].filter(Boolean).join(" ") ||
              data.displayName ||
              data.name ||
              data.email ||
              "";
            // Prefer a real label; never store bare uid here — row.displayName is the fallback.
            return [uid, label || null];
          } catch (err) {
            if (!isFirestorePermissionDenied(err)) {
              console.warn("[FdeRosterPage] user label lookup failed", uid, err);
            }
            return [uid, null];
          }
        }),
      );
      setUserNames(Object.fromEntries(nameEntries.filter(([, label]) => Boolean(label))));
      if (user?.uid) {
        try {
          setInvites(await listSentHierarchyInvites(user.uid));
        } catch (err) {
          if (!isFirestorePermissionDenied(err)) {
            console.warn("[FdeRosterPage] invite lookup failed", err);
          }
          setInvites([]);
        }
      }
    } catch (err) {
      setError(err?.message || "Failed to load role roster.");
    } finally {
      setLoading(false);
    }
  }, [institutionId, user?.uid]);

  const submitInvite = async (values) => {
    if (!institutionId) return;
    setInviteSaving(true);
    setError(null);
    try {
      const result = await generateHierarchyInvite({
        institutionId,
        targetLevel: Number(values.targetLevel),
        assignedRole: values.assignedRole,
        targetScopeId: institutionId,
        scopeLevel: Number(values.targetLevel),
        tciId: values.tciId || null,
        domainId: values.domainId || null,
        expiryDays: Number(values.expiryDays) || 14,
      });
      const absoluteUrl = `${window.location.origin}${result.inviteUrl}`;
      setGeneratedInviteUrl(absoluteUrl);
      setSuccess("Invitation created.");
      resetInviteForm();
      if (user?.uid) setInvites(await listSentHierarchyInvites(user.uid));
    } catch (err) {
      setError(err?.message || "Failed to generate invitation.");
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRevokeInvite = async (inviteCode) => {
    try {
      await revokeHierarchyInvite(inviteCode);
      if (user?.uid) setInvites(await listSentHierarchyInvites(user.uid));
    } catch (err) {
      setError(err?.message || "Failed to revoke invitation.");
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const scopeOptions = useMemo(() => {
    const instNode = hierarchyNodes.find((node) => node.level === 3);
    const institutionLabel =
      instNode?.name ||
      scopeNameById[institutionId] ||
      institutionId;
    const defaults = institutionId
      ? [{ id: instNode?.id || institutionId, label: institutionLabel }]
      : [];
    const extras = hierarchyNodes
      .filter((node) => node.level >= 4)
      .map((node) => {
        const levelLabel = PROMPT_LAYER_LABELS[node.level] || `Level ${node.level}`;
        return {
          id: node.id,
          label: `${node.name || scopeNameById[node.id] || node.id} (${levelLabel})`,
          level: node.level,
        };
      });
    return [...defaults, ...extras];
  }, [hierarchyNodes, institutionId, scopeNameById]);

  const formatAssignmentScope = useCallback(
    (row) => {
      const levelLabel =
        PROMPT_LAYER_LABELS[row.scopeLevel] ||
        SCOPE_LEVEL_OPTIONS.find((opt) => opt.value === Number(row.scopeLevel))?.label ||
        `Level ${row.scopeLevel}`;
      const scopeName =
        scopeNameById[row.scopeId] ||
        hierarchyNodes.find((node) => node.id === row.scopeId)?.name ||
        row.scopeId ||
        "—";
      return `${levelLabel}: ${scopeName}`;
    },
    [hierarchyNodes, scopeNameById],
  );

  const tciNameByIdMap = tciNameById;

  const domainNameById = useMemo(() => {
    const map = new Map();
    domains.forEach((domain) => map.set(domain.id, domain.name || domain.id));
    return map;
  }, [domains]);

  const openAssign = () => {
    reset({
      ...EMPTY_ASSIGN_FORM,
      scopeId: institutionId || "",
      scopeLevel: 3,
    });
    setAssignOpen(true);
  };

  const onAssign = async (values) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await assignFdeRole({
        uid: values.uid.trim(),
        role: values.role,
        scopeLevel: Number(values.scopeLevel),
        scopeId: values.scopeId.trim(),
        institutionId,
        tciId: values.role === "fde-c" ? values.tciId.trim() : null,
        domainId: values.role === "fde-dc" ? values.domainId.trim() : null,
        credentialAttestation:
          values.role === "fde-c" ? values.credentialAttestation.trim() : null,
        trainingComplete: Boolean(values.trainingComplete),
      });
      setSuccess("Role assigned.");
      setAssignOpen(false);
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to assign role.");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setError(null);
    try {
      await revokeFdeRole({ assignmentId: revokeTarget.assignmentId || revokeTarget.id });
      setSuccess("Assignment revoked.");
      setRevokeTarget(null);
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to revoke assignment.");
    } finally {
      setRevoking(false);
    }
  };

  if (!canManage) {
    return (
      <Alert severity="warning">You do not have permission to manage the role roster.</Alert>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Role roster"
        subtitle="Assign and track Clinical Annotator, TCI Manager, and Technical Readiness Lead roles within your institution."
        actions={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={() => {
                setGeneratedInviteUrl("");
                setInviteOpen(true);
              }}
            >
              Invite
            </Button>
            <Button startIcon={<Add />} variant="contained" onClick={openAssign}>
              Assign role
            </Button>
          </Box>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      {loading || tciRegistryLoading ? (
        <Loading page />
      ) : (
        <TableContainer component={Paper} sx={{ mt: 3, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>TCI / Domain</TableCell>
                <TableCell>Training</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!assignments.length ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No role assignments yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {userNames[row.uid] || row.displayName || row.uid}
                    </TableCell>
                    <TableCell>{FDE_ROLE_LABELS[row.role] || row.role}</TableCell>
                    <TableCell>{formatAssignmentScope(row)}</TableCell>
                    <TableCell>
                      {row.tciId
                        ? tciNameByIdMap.get(row.tciId) || row.tciId
                        : row.domainId
                          ? domainNameById.get(row.domainId) || row.domainId
                          : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.trainingComplete ? "Complete" : "Pending"}
                        color={row.trainingComplete ? "success" : "warning"}
                      />
                    </TableCell>
                    <TableCell>{FDE_STATUS_LABELS[row.status] || row.status}</TableCell>
                    <TableCell align="right">
                      {row.status !== "suspended" ? (
                        <Button size="small" color="error" onClick={() => setRevokeTarget(row)}>
                          Revoke
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign role</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
          <TextField
            label="Account ID"
            fullWidth
            error={Boolean(errors.uid)}
            helperText={errors.uid?.message || "Account ID of the person you are assigning."}
            {...register("uid", { required: "Account ID is required." })}
          />
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="fde-role-label">Role</InputLabel>
                <Select labelId="fde-role-label" label="Role" {...field}>
                  <MenuItem value="fde-t">{FDE_ROLE_LABELS["fde-t"]}</MenuItem>
                  <MenuItem value="fde-c">{FDE_ROLE_LABELS["fde-c"]}</MenuItem>
                  <MenuItem value="fde-dc">{FDE_ROLE_LABELS["fde-dc"]}</MenuItem>
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="scopeLevel"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="fde-scope-level-label">Scope level</InputLabel>
                <Select
                  labelId="fde-scope-level-label"
                  label="Scope level"
                  value={field.value}
                  onChange={(event) => field.onChange(Number(event.target.value))}
                >
                  {SCOPE_LEVEL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="scopeId"
            control={control}
            rules={{ required: "Scope is required." }}
            render={({ field }) => (
              <FormControl fullWidth error={Boolean(errors.scopeId)}>
                <InputLabel id="fde-scope-id-label">Scope node</InputLabel>
                <Select labelId="fde-scope-id-label" label="Scope node" {...field}>
                  {scopeOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          {watchedRole === "fde-c" ? (
            <>
              <Controller
                name="tciId"
                control={control}
                rules={{ required: `TCI is required for ${FDE_ROLE_LABELS["fde-c"]}.` }}
                render={({ field, fieldState }) => (
                  <TciRegistrySelect
                    label="TCI"
                    institutionId={institutionId}
                    value={field.value || ""}
                    onChange={field.onChange}
                    error={Boolean(fieldState.error)}
                    required
                  />
                )}
              />
              <TextField
                label="Credential attestation"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(errors.credentialAttestation)}
                helperText={
                  errors.credentialAttestation?.message ||
                  "Required — attestation that the assignee is qualified for this TCI."
                }
                {...register("credentialAttestation", {
                  required: `Credential attestation is required for ${FDE_ROLE_LABELS["fde-c"]}.`,
                })}
              />
            </>
          ) : null}
          {watchedRole === "fde-dc" ? (
            <Controller
              name="domainId"
              control={control}
              rules={{ required: `Domain is required for ${FDE_ROLE_LABELS["fde-dc"]}.` }}
              render={({ field }) => (
                <FormControl fullWidth error={Boolean(errors.domainId)}>
                  <InputLabel id="fde-domain-label">Clinical domain</InputLabel>
                  <Select labelId="fde-domain-label" label="Clinical domain" {...field}>
                    {domains.map((domain) => (
                      <MenuItem key={domain.id} value={domain.id}>
                        {domain.name || domain.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          ) : null}
          <Controller
            name="trainingComplete"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="fde-training-label">Training status</InputLabel>
                <Select
                  labelId="fde-training-label"
                  label="Training status"
                  value={field.value ? "complete" : "pending"}
                  onChange={(event) => field.onChange(event.target.value === "complete")}
                >
                  <MenuItem value="pending">Pending training</MenuItem>
                  <MenuItem value="complete">Training complete</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleSubmit(onAssign)}>
            Assign
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        title="Revoke assignment?"
        message="This suspends the assignment. The user loses these role permissions until reassigned."
        confirmLabel="Revoke"
        confirmColor="error"
        loading={revoking}
        onConfirm={handleRevoke}
      />

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate hierarchy invitation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Controller
              name="assignedRole"
              control={inviteControl}
              render={({ field }) => (
                <TextField {...field} select label="Role">
                  <MenuItem value="fde-c">{FDE_ROLE_LABELS["fde-c"]}</MenuItem>
                  <MenuItem value="fde-dc">{FDE_ROLE_LABELS["fde-dc"]}</MenuItem>
                  <MenuItem value="fde-t">{FDE_ROLE_LABELS["fde-t"]}</MenuItem>
                  <MenuItem value="institution-admin">Institution admin</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="targetLevel"
              control={inviteControl}
              render={({ field }) => (
                <TextField {...field} select label="Target level">
                  {SCOPE_LEVEL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="tciId"
              control={inviteControl}
              render={({ field }) => (
                <TextField {...field} select label={`TCI (${FDE_ROLE_LABELS["fde-c"]})`}>
                  <MenuItem value="">None</MenuItem>
                  {tcis.map((tci) => (
                    <MenuItem key={tci.id} value={tci.id}>
                      {tci.name || tci.id}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="domainId"
              control={inviteControl}
              render={({ field }) => (
                <TextField {...field} select label={`Domain (${FDE_ROLE_LABELS["fde-dc"]})`}>
                  <MenuItem value="">None</MenuItem>
                  {domains.map((domain) => (
                    <MenuItem key={domain.id} value={domain.id}>
                      {domain.name || domain.id}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {generatedInviteUrl ? (
              <Alert severity="success">Share this link: {generatedInviteUrl}</Alert>
            ) : null}
            {invites.length ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Recent invitations
                </Typography>
                {invites.slice(0, 5).map((row) => (
                  <Stack key={row.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip size="small" label={row.status} />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {row.assignedRole} · {row.inviteCode}
                    </Typography>
                    {row.status === "pending" ? (
                      <Button size="small" color="error" onClick={() => handleRevokeInvite(row.inviteCode)}>
                        Revoke
                      </Button>
                    ) : null}
                  </Stack>
                ))}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Close</Button>
          <LoadingButton variant="contained" loading={inviteSaving} onClick={handleInviteSubmit(submitInvite)}>
            Generate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FdeRosterPage;
